import { Card, CardContent } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import {
  Column,
  ColorDynamic,
  Columns,
  Inline,
  Stack,
} from '@superdispatch/ui';
import { ReactNode, useCallback } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { LoadsInvalidationContext } from '../data/LoadsAPI';

export type ViewLoadSectionName =
  | 'pickup'
  | 'delivery'
  | 'vehicles'
  | 'instructions'
  | 'expenses'
  | 'subhauler'
  | 'payment-status'
  | 'payment-agreement'
  | 'customer'
  | 'attachments'
  | 'notes';

const pulse = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
  100% {
    opacity: 1;
  }
`;

/* Covers the element with skeleton lines while keeping its current size */
const skeletonOverlay = css`
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 4px;
  }

  &::before {
    z-index: 1;
    background: ${ColorDynamic.White};
  }

  &::after {
    z-index: 2;
    inset: 16px;
    background: repeating-linear-gradient(
      to bottom,
      ${ColorDynamic.Silver200} 0 16px,
      transparent 16px 32px
    );
    animation: ${pulse} 1.5s ease-in-out 0.5s infinite;
  }
`;

/* Keeps the loaded cards mounted at their current size while the load
refetches and covers each of them with skeleton lines, so the layout doesn't jump */
export const ViewLoadPageRefetching = styled.div<{ $isRefetching: boolean }>`
  ${({ $isRefetching }) =>
    $isRefetching &&
    css`
      pointer-events: none;

      & .MuiCard-root {
        ${skeletonOverlay}
      }

      & .MuiCard-root .MuiCard-root::before,
      & .MuiCard-root .MuiCard-root::after {
        content: none;
      }
    `}
`;

const ViewLoadSectionRefetching = styled.div<{ $isRefetching: boolean }>`
  ${({ $isRefetching }) =>
    $isRefetching &&
    css`
      pointer-events: none;
      ${skeletonOverlay}
    `}
`;

interface ViewLoadSectionProps {
  name: ViewLoadSectionName;
  isUpdating: boolean;
  onUpdate: (name: ViewLoadSectionName) => void;
  children: ReactNode;
}

/* Shows a skeleton only over this section while the load refetches
after an update made from inside it */
export function ViewLoadSection({
  name,
  isUpdating,
  onUpdate,
  children,
}: ViewLoadSectionProps) {
  const handleInvalidate = useCallback(() => {
    onUpdate(name);
  }, [name, onUpdate]);

  return (
    <LoadsInvalidationContext.Provider value={handleInvalidate}>
      <ViewLoadSectionRefetching
        $isRefetching={isUpdating}
        aria-busy={isUpdating}
      >
        {children}
      </ViewLoadSectionRefetching>
    </LoadsInvalidationContext.Provider>
  );
}

function VenueSkeleton() {
  return (
    <Stack space="small">
      <Inline space="xsmall" verticalAlign="center">
        <Skeleton variant="circle" width={24} height={24} />
        <Skeleton variant="text" width={140} height={32} />
      </Inline>
      <Stack space="xsmall">
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="50%" />
        <Skeleton variant="text" width="55%" />
      </Stack>
    </Stack>
  );
}

function SectionCardSkeleton({ rows }: { rows: number }) {
  return (
    <Card>
      <CardContent>
        <Stack space="small">
          <Skeleton variant="text" width={180} height={32} />
          <Stack space="xsmall">
            {Array.from({ length: rows }, (_, index) => (
              <Skeleton key={index} variant="text" width="100%" />
            ))}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function ViewLoadPageLoading() {
  return (
    <Columns space="small" collapseBelow="desktop">
      <Column width={['fluid', '2/3']}>
        <Stack space="small">
          <Card>
            <CardContent>
              <Stack space="large">
                <Columns space="small" collapseBelow="tablet">
                  <Column width="fluid">
                    <VenueSkeleton />
                  </Column>
                  <Column width="fluid">
                    <VenueSkeleton />
                  </Column>
                </Columns>

                <Stack space="small">
                  <Skeleton variant="text" width={140} height={32} />
                  <Skeleton variant="rect" width="100%" height={72} />
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <SectionCardSkeleton rows={3} />
          <SectionCardSkeleton rows={2} />
        </Stack>
      </Column>

      <Column width="1/3">
        <Stack space="small">
          <SectionCardSkeleton rows={3} />
          <SectionCardSkeleton rows={5} />
          <SectionCardSkeleton rows={4} />
        </Stack>
      </Column>
    </Columns>
  );
}
